import { c as _c } from "react/compiler-runtime";
/**
 * Wiki Settings Data Hooks
 *
 * TanStack DB-backed hooks for managing wiki auto-refresh settings on the
 * frontend. The hooks keep the existing React Query fetch/cache path as the
 * source of server truth while React DB owns live optimistic row updates.
 */

import type { WikiSettingResponse } from '@/lib/wiki-settings/schema';
import { queryCollectionOptions } from '@tanstack/query-db-collection';
import type { Collection, Transaction, TransactionWithMutations } from '@tanstack/react-db';
import { createCollection, debounceStrategy, useLiveQuery, usePacedMutations } from '@tanstack/react-db';
import type { QueryClient } from '@tanstack/react-query';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { wikiSettingsQueryKey } from './wiki-settings-query-key';
import { WikiRepoSyncLatestRunSchema } from '@/lib/wiki-settings/schema';
const WIKI_SETTINGS_ROUTE = '/api/wiki/settings';
const DEFAULT_FETCH_ERROR = 'Failed to fetch wiki settings';
const DEFAULT_UPDATE_ERROR = 'Failed to update wiki settings';
const WIKI_SETTINGS_STALE_TIME_MS = 5 * 60 * 1000;
const WIKI_SETTINGS_DEBOUNCE_MS = 250;
const WIKI_REPO_SYNC_POLL_INTERVAL_MS = 2_000;
type WikiSettingsInput = {
  autoRefreshEnabled?: boolean;
  autoRefreshSchedule?: 'daily' | 'weekly' | 'monthly';
  customInstructions?: string | null;
  repoSyncEnabled?: boolean;
  repoSyncPath?: string;
};
type WikiSettingsUpdate = WikiSettingsInput | ((settings: WikiSettingResponse) => WikiSettingsInput);
type WikiSettingsUpdateVariables = {
  update: WikiSettingsUpdate;
};
type WikiSettingsCollection = Collection<WikiSettingResponse, string>;
const wikiSettingsCollectionsByQueryClient = new WeakMap<QueryClient, Map<string, WikiSettingsCollection>>();
async function fetchWikiSettings(installationId: number | null, repositoryId: number | null, options: {
  signal?: AbortSignal;
} = {}): Promise<WikiSettingResponse | null> {
  if (!installationId || !repositoryId) {
    return null;
  }
  const response = await fetch(`${WIKI_SETTINGS_ROUTE}/${installationId}/${repositoryId}`, {
    method: 'GET',
    credentials: 'include',
    signal: options.signal,
    headers: {
      Accept: 'application/json'
    }
  });
  if (response.status === 404) {
    return null;
  }
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    console.error('[useWikiSettings] Failed to parse response payload');
    throw new Error(DEFAULT_FETCH_ERROR);
  }
  if (!response.ok) {
    const payloadError = payload && typeof payload === 'object' && 'error' in payload ? (payload as Record<string, unknown>).error : null;
    const message = typeof payloadError === 'object' && payloadError !== null && 'message' in payloadError ? (payloadError as Record<string, unknown>).message as string | undefined : undefined;
    throw new Error(message ?? DEFAULT_FETCH_ERROR);
  }
  return payload as WikiSettingResponse;
}
async function patchWikiSettings(installationId: number, repositoryId: number, newSettings: WikiSettingsInput): Promise<WikiSettingResponse> {
  const response = await fetch(`${WIKI_SETTINGS_ROUTE}/${installationId}/${repositoryId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(newSettings)
  });
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    console.error('[useWikiSettings] Failed to parse update response');
    throw new Error(DEFAULT_UPDATE_ERROR);
  }
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null && 'error' in payload ? ((payload as Record<string, unknown>).error as Record<string, unknown>)?.message as string | undefined : undefined;
    throw new Error(message ?? DEFAULT_UPDATE_ERROR);
  }
  return payload as WikiSettingResponse;
}
async function postWikiRepoSyncNow(installationId: number, repositoryId: number): Promise<NonNullable<WikiSettingResponse['latestRepoSyncRun']>> {
  const response = await fetch(`${WIKI_SETTINGS_ROUTE}/${installationId}/${repositoryId}/sync-now`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json'
    }
  });
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    throw new Error('Failed to request wiki repo sync');
  }
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null && 'error' in payload ? ((payload as Record<string, unknown>).error as Record<string, unknown>)?.message as string | undefined : undefined;
    throw new Error(message ?? 'Failed to request wiki repo sync');
  }
  const parsedRun = WikiRepoSyncLatestRunSchema.safeParse(typeof payload === 'object' && payload !== null && 'latestRepoSyncRun' in payload ? (payload as Record<string, unknown>).latestRepoSyncRun : null);
  if (!parsedRun.success) {
    throw new Error('Wiki sync was queued, but its status could not be loaded');
  }
  return parsedRun.data;
}
function getDefaultWikiSettings(installationId: number, repositoryId: number): WikiSettingResponse {
  return {
    repositoryId,
    installationId,
    autoRefreshEnabled: false,
    autoRefreshSchedule: 'weekly',
    customInstructions: null,
    repoSyncAvailable: false,
    repoSyncEnabled: false,
    repoSyncPath: '.cubic/wiki',
    lastSyncedWikiId: null,
    lastRepoSyncAt: null,
    latestRepoSyncRun: null,
    lastAutoRefreshAt: null,
    lastManualRefreshAt: null,
    canManualRefreshAt: null,
    hasActiveSubscription: false,
    hasScanAccess: false
  };
}
function getWikiSettingsCollectionKey(settings: Pick<WikiSettingResponse, 'installationId' | 'repositoryId'>) {
  return `${settings.installationId}:${settings.repositoryId}`;
}
function includeDefinedWikiSettingsFields(source: WikiSettingsInput): WikiSettingsInput {
  const result: WikiSettingsInput = {};
  if (source.autoRefreshEnabled !== undefined) {
    result.autoRefreshEnabled = source.autoRefreshEnabled;
  }
  if (source.autoRefreshSchedule !== undefined) {
    result.autoRefreshSchedule = source.autoRefreshSchedule;
  }
  if (source.customInstructions !== undefined) {
    result.customInstructions = source.customInstructions;
  }
  if (source.repoSyncEnabled !== undefined) {
    result.repoSyncEnabled = source.repoSyncEnabled;
  }
  if (source.repoSyncPath !== undefined) {
    result.repoSyncPath = source.repoSyncPath;
  }
  return result;
}
function getMergedUpdateInput(transaction: TransactionWithMutations<WikiSettingResponse>) {
  const mergedChanges: WikiSettingsInput = {};
  for (const mutation of transaction.mutations) {
    Object.assign(mergedChanges, includeDefinedWikiSettingsFields(mutation.changes as WikiSettingsInput));
  }
  return mergedChanges;
}
function resolveWikiSettingsUpdate(update: WikiSettingsUpdate, settings: WikiSettingResponse): WikiSettingsInput {
  return typeof update === 'function' ? update(settings) : update;
}
function getWikiSettingsToastId(settings: Pick<WikiSettingResponse, 'installationId' | 'repositoryId'>) {
  return `wiki-settings:${settings.installationId}:${settings.repositoryId}`;
}
function getWikiSettingsCollection(queryClient: QueryClient, installationId: number, repositoryId: number) {
  let collections = wikiSettingsCollectionsByQueryClient.get(queryClient);
  if (!collections) {
    collections = new Map();
    wikiSettingsCollectionsByQueryClient.set(queryClient, collections);
  }
  const collectionKey = `${installationId}:${repositoryId}`;
  const existingCollection = collections.get(collectionKey);
  if (existingCollection) {
    return existingCollection;
  }
  const collection = createCollection(queryCollectionOptions({
    id: `wiki-settings:${installationId}:${repositoryId}`,
    queryKey: wikiSettingsQueryKey(installationId, repositoryId),
    queryFn: ({
      signal
    }) => fetchWikiSettings(installationId, repositoryId, {
      signal
    }),
    select: (settings: WikiSettingResponse | null) => [settings ?? getDefaultWikiSettings(installationId, repositoryId)],
    queryClient,
    getKey: getWikiSettingsCollectionKey,
    staleTime: WIKI_SETTINGS_STALE_TIME_MS,
    refetchInterval: query => query.state.data?.latestRepoSyncRun?.status === 'pending' ? WIKI_REPO_SYNC_POLL_INTERVAL_MS : false
  }));
  collections.set(collectionKey, collection);
  return collection;
}
export function useLiveWikiSettings(props: {
  installationId: number | null;
  repositoryId: number | null;
}) {
  const queryClient = useQueryClient();
  const mountedRef = useRef(true);
  const [pendingTransactionCount, setPendingTransactionCount] = useState(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const collection = useMemo(() => {
    if (!props.installationId || !props.repositoryId) {
      return null;
    }
    return getWikiSettingsCollection(queryClient, props.installationId, props.repositoryId);
  }, [props.installationId, props.repositoryId, queryClient]);
  const rowKey = useMemo(() => {
    if (!props.installationId || !props.repositoryId) {
      return null;
    }
    return `${props.installationId}:${props.repositoryId}`;
  }, [props.installationId, props.repositoryId]);
  const liveQuery = useLiveQuery(() => collection, [collection]);
  const settings = collection ? liveQuery.data?.[0] : null;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const queryKey = useMemo(() => wikiSettingsQueryKey(props.installationId, props.repositoryId), [props.installationId, props.repositoryId]);
  const fetchingCount = useIsFetching({
    queryKey,
    exact: true
  });
  const mutationMetadata = useMemo(() => ({
    rowKey: rowKey ?? 'disabled'
  }), [rowKey]);
  const mutate = usePacedMutations<WikiSettingsUpdateVariables, WikiSettingResponse>({
    metadata: mutationMetadata,
    strategy: debounceStrategy({
      wait: WIKI_SETTINGS_DEBOUNCE_MS
    }),
    onMutate: ({
      update
    }) => {
      if (!collection || !rowKey || !settingsRef.current) {
        return;
      }
      collection.update(rowKey, draft => {
        const changes = includeDefinedWikiSettingsFields(resolveWikiSettingsUpdate(update, draft));
        Object.assign(draft, changes);
      });
    },
    mutationFn: async ({
      transaction
    }) => {
      const firstMutation = transaction.mutations[0];
      if (!firstMutation?.modified) {
        throw new Error('No mutation data found in transaction');
      }
      const installationId = firstMutation.modified.installationId;
      const repositoryId = firstMutation.modified.repositoryId;
      const changes_0 = getMergedUpdateInput(transaction);
      const updatedSettings = await patchWikiSettings(installationId, repositoryId, changes_0);
      queryClient.setQueryData(wikiSettingsQueryKey(installationId, repositoryId), updatedSettings);
      return updatedSettings;
    }
  });
  const updateSettings = useCallback((update_0: WikiSettingsUpdate): Transaction<WikiSettingResponse> | undefined => {
    if (!collection || !rowKey) {
      return undefined;
    }
    const currentSettings = collection.get(rowKey) ?? settingsRef.current;
    if (!currentSettings) {
      return undefined;
    }
    const updatedFields = Object.keys(includeDefinedWikiSettingsFields(resolveWikiSettingsUpdate(update_0, currentSettings)));
    if (updatedFields.length === 0) {
      return undefined;
    }
    const toastId = getWikiSettingsToastId(currentSettings);
    toast.success('Wiki settings updated', {
      id: toastId
    });
    setPendingTransactionCount(count => count + 1);
    const transaction_0 = mutate({
      update: update_0
    });
    void transaction_0.isPersisted.promise.then(() => undefined).catch((error: Error) => {
      toast.error(error.message || DEFAULT_UPDATE_ERROR, {
        id: toastId
      });
    }).finally(() => {
      if (mountedRef.current) {
        setPendingTransactionCount(count_0 => Math.max(0, count_0 - 1));
      }
    });
    return transaction_0;
  }, [collection, mutate, rowKey]);
  const syncNow = useCallback(async () => {
    if (!props.installationId || !props.repositoryId) {
      return;
    }
    const toastId_0 = `wiki-repo-sync:${props.installationId}:${props.repositoryId}`;
    try {
      const pendingRun = await postWikiRepoSyncNow(props.installationId, props.repositoryId);
      queryClient.setQueryData<WikiSettingResponse | null>(queryKey, currentSettings_0 => {
        return currentSettings_0 ? {
          ...currentSettings_0,
          latestRepoSyncRun: pendingRun
        } : currentSettings_0;
      });
      toast.success('Wiki sync queued', {
        id: toastId_0
      });
    } catch (error_0) {
      toast.error(error_0 instanceof Error ? error_0.message : 'Failed to request wiki repo sync', {
        id: toastId_0
      });
    }
  }, [props.installationId, props.repositoryId, queryClient, queryKey]);
  const loadError = settings === undefined ? collection?.utils.lastError ?? null : null;
  const isInitialLoading = Boolean(collection && liveQuery.isLoading && settings === undefined);
  return {
    settings,
    error: loadError,
    isLoading: liveQuery.isLoading,
    isFetching: fetchingCount > 0,
    isInitialLoading,
    isPersisting: pendingTransactionCount > 0,
    updateSettings,
    syncNow
  };
}
export function useQueryWikiSettings(props) {
  const $ = _c(6);
  const {
    settings,
    error,
    isFetching,
    isLoading,
    syncNow
  } = useLiveWikiSettings(props);
  let t0;
  if ($[0] !== error || $[1] !== isFetching || $[2] !== isLoading || $[3] !== settings || $[4] !== syncNow) {
    t0 = {
      settings,
      isLoading,
      isFetching,
      error,
      syncNow
    };
    $[0] = error;
    $[1] = isFetching;
    $[2] = isLoading;
    $[3] = settings;
    $[4] = syncNow;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}